/**
 * Dynamic Companion Service
 * Generates procedural companions/NPCs based on current location and story context
 */

import { v4 as uuidv4 } from 'uuid';

export interface DynamicCompanion {
  id: string;
  name: string;
  archetype: string;
  personality: string[];
  voiceId: string;
  karmaThreshold: number;
  location: string;
  backstory: string;
  relationshipLevel: number;
  skills: string[];
  duration: 'temporary' | 'permanent' | 'quest';
  leaveCondition?: string;
  appearance: string;
  dialogue: string[];
}

// Companion archetypes for different locations
const COMPANION_ARCHETYPES: Record<string, string[]> = {
  downtown: ['street_samurai', 'fixer', 'underground_journalist', 'black_market_trader'],
  corporate: ['corp_defector', 'netrunner', 'executive_assistant', 'security_breach'],
  outskirts: ['nomad_trader', 'medtech', 'scavenger', 'wilderness_guide'],
  temple: ['cult_mystic', 'rogue_ai', 'data_monk', 'spiritual_hacker'],
  underground: ['hacker', 'smuggler', 'resistance_fighter', 'data_thief'],
  market: ['trader', 'information_broker', 'tech_vendor', 'black_marketeer'],
  void: ['wanderer', 'glitch_entity', 'memory_fragment', 'lost_ai']
};

// Voice assignments for archetypes
const VOICE_MAPPING: Record<string, string> = {
  street_samurai: 'Google UK English Male',
  fixer: 'Google US English',
  netrunner: 'Google UK English Female',
  corp_defector: 'Microsoft Zira',
  rogue_ai: 'Google UK English Female',
  medtech: 'Microsoft David',
  nomad_trader: 'Google US English',
  cult_mystic: 'Google UK English Female',
  hacker: 'Google US English',
  trader: 'Microsoft Mark',
  wanderer: 'Google UK English Male'
};

class DynamicCompanionService {
  /**
   * Generate a companion for a specific location
   */
  async generateCompanionForLocation(
    location: string,
    worldKarma: number,
    playerKarma: number,
    genre: string = 'cyberpunk'
  ): Promise<DynamicCompanion> {
    // Determine location type from name
    const locationType = this.categorizeLocation(location);

    // Select random archetype suitable for location
    const archetypes = COMPANION_ARCHETYPES[locationType] || COMPANION_ARCHETYPES.void;
    const archetype = archetypes[Math.floor(Math.random() * archetypes.length)];

    // Generate name and personality procedurally
    const companionData = this.generateProceduralCompanion(archetype, location);

    return {
      id: uuidv4(),
      name: companionData.name,
      archetype,
      personality: companionData.personality,
      voiceId: VOICE_MAPPING[archetype] || 'Google US English',
      karmaThreshold: this.calculateKarmaThreshold(archetype, worldKarma),
      location,
      backstory: companionData.backstory,
      relationshipLevel: 0,
      skills: companionData.skills,
      duration: this.determineDuration(archetype),
      leaveCondition: companionData.leaveCondition,
      appearance: companionData.appearance,
      dialogue: companionData.initialDialogue
    };
  }

  /**
   * Generate multiple companions for a location
   */
  async generateCompanionsForScene(
    location: string,
    worldKarma: number,
    playerKarma: number,
    count: number = 2
  ): Promise<DynamicCompanion[]> {
    const companions: DynamicCompanion[] = [];

    for (let i = 0; i < count; i++) {
      try {
        const companion = await this.generateCompanionForLocation(
          location,
          worldKarma,
          playerKarma
        );
        companions.push(companion);
      } catch (error) {
        console.error('[DynamicCompanion] Failed to generate companion:', error);
      }
    }

    return companions;
  }

  /**
   * Procedural companion generation
   */
  private generateProceduralCompanion(archetype: string, location: string): any {
    const names = this.generateName(archetype);
    const personalities = this.generatePersonality(archetype);

    return {
      name: names,
      personality: personalities,
      backstory: `A ${archetype.replace('_', ' ')} who operates in ${location}. Has a mysterious past.`,
      skills: this.getArchetypeSkills(archetype),
      leaveCondition: `When the situation in ${location} changes`,
      appearance: `Typical ${archetype.replace('_', ' ')} appearance with cybernetic enhancements`,
      initialDialogue: [
        `Greetings, stranger. Welcome to ${location}.`,
        `I've been expecting someone like you.`
      ]
    };
  }

  /**
   * Generate procedural name based on archetype
   */
  private generateName(archetype: string): string {
    const prefixes = ['Neo', 'Cyber', 'Data', 'Ghost', 'Shadow', 'Chrome', 'Neon', 'Vex', 'Raven', 'Eclipse'];
    const suffixes = ['Runner', 'Jack', 'Weaver', 'Blade', 'Wraith', 'Pulse', 'Storm', 'Fang', 'Cipher'];

    const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
    const suffix = suffixes[Math.floor(Math.random() * suffixes.length)];

    // Add archetype-specific flavor
    if (archetype.includes('medtech')) return `Dr. ${prefix}${suffix}`;
    if (archetype.includes('mystic')) return `${prefix} the Awakened`;
    if (archetype.includes('trader')) return `${prefix} ${suffix} Trading Co.`;

    return `${prefix}-${suffix}`;
  }

  /**
   * Generate personality traits
   */
  private generatePersonality(archetype: string): string[] {
    const traitPool: Record<string, string[]> = {
      street_samurai: ['loyal', 'disciplined', 'honorable', 'deadly'],
      netrunner: ['analytical', 'paranoid', 'brilliant', 'isolated'],
      medtech: ['compassionate', 'precise', 'ethical', 'overwhelmed'],
      trader: ['shrewd', 'opportunistic', 'charming', 'greedy'],
      mystic: ['enigmatic', 'spiritual', 'wise', 'unsettling'],
      hacker: ['rebellious', 'clever', 'anti-authority', 'resourceful']
    };

    const baseTraits = traitPool[archetype] || ['mysterious', 'capable', 'independent'];

    // Randomly select 3-4 traits
    const count = 3 + Math.floor(Math.random() * 2);
    const selected: string[] = [];

    while (selected.length < count && selected.length < baseTraits.length) {
      const trait = baseTraits[Math.floor(Math.random() * baseTraits.length)];
      if (!selected.includes(trait)) {
        selected.push(trait);
      }
    }

    return selected;
  }

  /**
   * Get skills for archetype
   */
  private getArchetypeSkills(archetype: string): string[] {
    const skillMap: Record<string, string[]> = {
      street_samurai: ['Close Combat', 'Weapon Mastery', 'Intimidation'],
      netrunner: ['Hacking', 'Data Analysis', 'System Infiltration'],
      corp_defector: ['Corporate Knowledge', 'Security Bypass', 'Insider Info'],
      medtech: ['Healing', 'Cybernetic Repair', 'Medicine'],
      trader: ['Bargaining', 'Appraisal', 'Black Market Access'],
      hacker: ['Code Breaking', 'Exploits', 'Digital Stealth'],
      mystic: ['Karma Reading', 'Spiritual Guidance', 'Reality Manipulation']
    };

    return skillMap[archetype] || ['Survival', 'Navigation', 'Lore'];
  }

  /**
   * Categorize location type
   */
  private categorizeLocation(location: string): string {
    const lower = location.toLowerCase();

    if (lower.includes('plaza') || lower.includes('downtown') || lower.includes('street')) return 'downtown';
    if (lower.includes('corp') || lower.includes('tower') || lower.includes('office')) return 'corporate';
    if (lower.includes('outskirts') || lower.includes('wasteland') || lower.includes('border')) return 'outskirts';
    if (lower.includes('temple') || lower.includes('shrine') || lower.includes('sanctum')) return 'temple';
    if (lower.includes('underground') || lower.includes('sewer') || lower.includes('tunnel')) return 'underground';
    if (lower.includes('market') || lower.includes('bazaar') || lower.includes('exchange')) return 'market';
    if (lower.includes('void') || lower.includes('nowhere') || lower.includes('liminal')) return 'void';

    return 'downtown'; // Default
  }

  /**
   * Calculate karma threshold for companion availability
   */
  private calculateKarmaThreshold(archetype: string, worldKarma: number): number {
    // Some companions only appear at certain karma levels
    const thresholds: Record<string, number> = {
      street_samurai: -2,  // Slightly dark
      corp_defector: 5,    // Positive karma
      mystic: 7,           // High karma
      trader: 0,           // Neutral
      hacker: -5,          // Dark side
      medtech: 3           // Positive
    };

    return thresholds[archetype] || 0;
  }

  /**
   * Determine how long companion stays
   */
  private determineDuration(archetype: string): 'temporary' | 'permanent' | 'quest' {
    // Most companions are temporary (scene-based)
    // Some can become permanent based on relationship
    // Quest-based leave after objective complete

    if (archetype.includes('trader') || archetype.includes('vendor')) {
      return 'temporary'; // Merchants move around
    }

    if (archetype.includes('mystic') || archetype.includes('guide')) {
      return 'quest'; // Leave after helping with something
    }

    // Default: temporary (stay for current location)
    return 'temporary';
  }

  /**
   * Check if companion should leave based on conditions
   */
  shouldCompanionLeave(
    companion: DynamicCompanion,
    newLocation: string,
    storyEvents: string[]
  ): boolean {
    // Temporary companions leave when location changes
    if (companion.duration === 'temporary' && newLocation !== companion.location) {
      return true;
    }

    // Quest companions leave if leave condition matches story events
    if (companion.duration === 'quest' && companion.leaveCondition) {
      const conditionMet = storyEvents.some(event =>
        event.toLowerCase().includes(companion.leaveCondition!.toLowerCase())
      );
      if (conditionMet) {
        return true;
      }
    }

    return false;
  }
}

export const dynamicCompanionService = new DynamicCompanionService();
